import React, { useEffect, useState } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import { useAuth } from '../auth/AuthContext';
import {
  type AdminConfirmationPopupProps,
  AdminConfirmationPopup
} from '../components/AdminConfirmationPopup';

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const EmailNewsletter: React.FC = () => {
  const { user } = useAuth();

  const [emailList, setEmailList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [confirmation, setConfirmation] =
    useState<AdminConfirmationPopupProps | null>(null);

  const fetchEmailList = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/newsletter/emails', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch email list');
      const data = await res.json();
      setEmailList(data.emails || []);
    } catch (e) {
      setError('Failed to fetch email list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailList();
  }, [user]);

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (emailList.includes(email)) {
      setError('This email is already in the list.');
      return;
    }

    setIsAdding(true);
    setError(null);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const res = await fetch('/api/admin/newsletter/emails/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error('Failed to add email');
      setEmailList((prev) => [...prev, email]);
      setNewEmail('');
    } catch (e) {
      console.error('Error adding email:', e);
      setError(e instanceof Error ? e.message : 'Failed to add email');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveEmail = (email: string) => {
    setConfirmation({
      innerHTML: (
        <div className="w-full flex flex-col items-center gap-4">
          <p className="w-full text-lg text-center">
            Remove <strong>{email}</strong> from the newsletter list?
          </p>
        </div>
      ),
      onConfirm: async () => {
        setConfirmation(null);
        try {
          if (!user) throw new Error('Not authenticated');
          const token = await user.getIdToken();
          const res = await fetch('/api/admin/newsletter/emails/remove', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ email })
          });
          if (!res.ok) throw new Error('Failed to remove email');
          setEmailList((prev) => prev.filter((e) => e !== email));
        } catch (e) {
          console.error('Error removing email:', e);
          setError('Failed to remove email');
        }
      },
      onCancel: () => setConfirmation(null)
    });
  };

  const filteredEmails = emailList.filter((email) =>
    email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 h-full flex flex-col gap-6">
      <h2 className="text-2xl font-bold">Newsletter Recipient List</h2>

      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded-md">{error}</div>
      )}

      {/* Search */}
      <input
        type="search"
        placeholder="Search emails..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full border border-light-border p-2 rounded-md"
      />

      {/* Add new email form */}
      <form onSubmit={handleAddEmail} className="flex gap-2">
        <input
          type="email"
          placeholder="name@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="flex-1 border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
        />
        <button
          type="submit"
          disabled={isAdding || !newEmail.trim()}
          className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap"
        >
          {isAdding ? 'Adding...' : 'Add Email'}
        </button>
      </form>

      {/* Email list table */}
      <div className="flex-1 overflow-y-auto border border-light-border rounded-md">
        {loading ? (
          <LoadingAnimatedInline />
        ) : filteredEmails.length === 0 ? (
          <p className="p-6 text-center text-gray-500">
            {emailList.length === 0
              ? 'No emails in the newsletter list.'
              : 'No emails match your search.'}
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead className="bg-light-main text-light-text-alt sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Email</th>
                <th className="px-4 py-3 text-left font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmails.map((email) => (
                <tr key={email} className="border-b border-light-border hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm break-all">{email}</td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="text-red-500 hover:text-red-700 font-semibold transition-colors text-lg"
                      title="Remove email"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmation && <AdminConfirmationPopup {...confirmation} />}
    </div>
  );
};

export default EmailNewsletter;
